import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { IconButton, Collapse, Typography, Button } from '@material-ui/core';
import { Close as CloseIcon, Warning as WarningIcon } from '@material-ui/icons';
import { Link } from 'react-router-dom';

import * as OrganizationActions from 'app/main/organizations/store/actions';

// Feature: 001-org-multi-tenancy - License Warning Banner (T044, T045)

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		position: 'relative',
		zIndex: 1000
	},
	warningAlert: {
		backgroundColor: '#fff3e0',
		borderBottom: '1px solid #ff9800'
	},
	urgentAlert: {
		backgroundColor: '#fff8e1',
		borderBottom: '1px solid #f57c00'
	},
	criticalAlert: {
		backgroundColor: '#ffebee',
		borderBottom: '1px solid #f44336'
	},
	expiredAlert: {
		backgroundColor: '#ffcdd2',
		borderBottom: '1px solid #b71c1c'
	},
	compactBanner: {
		padding: '4px 16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer'
	},
	bannerText: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	}
}));

const LicenseWarningBanner = ({ compact = false }) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const [dismissed, setDismissed] = useState(false);
	const [expanded, setExpanded] = useState(!compact);

	const user = useSelector(({ auth }) => auth.user);
	const myOrganization = useSelector(({ organizations }) => organizations?.organization?.myOrganization);
	const organizationModeEnabled = useSelector(({ organizations }) => organizations?.organization?.organizationModeEnabled);

	// Fetch organization data on mount
	useEffect(() => {
		if (user?.organizationId) {
			dispatch(OrganizationActions.getMyOrganization());
			dispatch(OrganizationActions.checkOrganizationModeEnabled());
		}
	}, [dispatch, user?.organizationId]);

	// Don't show banner if:
	// - Organization mode is not enabled
	// - User is not part of an organization
	// - User is a super-admin (platform level)
	// - Banner was dismissed
	if (!organizationModeEnabled || !user?.organizationId || !myOrganization || dismissed) {
		return null;
	}

	// Check if user has super-admin role (they shouldn't have organizationId, but just in case)
	if (user?.role?.includes('super-admin')) {
		return null;
	}

	// Calculate warning level
	const getWarningInfo = () => {
		const license = myOrganization?.license;
		const status = myOrganization?.status;

		// Check organization status first
		if (status === 'suspended') {
			return {
				level: 'critical',
				title: 'Organization Suspended',
				message: 'Your organization has been suspended. Write operations are disabled.',
				className: classes.criticalAlert
			};
		}

		if (status === 'disabled') {
			return {
				level: 'expired',
				title: 'Organization Disabled',
				message: 'Your organization has been disabled. Please contact your administrator.',
				className: classes.expiredAlert
			};
		}

		// Check license expiration
		if (license?.expiresAt) {
			const now = new Date();
			const expiresAt = new Date(license.expiresAt);
			const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

			if (daysRemaining <= 0) {
				return {
					level: 'expired',
					title: 'License Expired',
					message: 'Your organization\'s license has expired. Write operations are disabled.',
					className: classes.expiredAlert
				};
			} else if (daysRemaining <= 3) {
				return {
					level: 'critical',
					title: 'License Expiring Soon',
					message: `Your organization's license expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Please contact your administrator.`,
					className: classes.criticalAlert
				};
			} else if (daysRemaining <= 7) {
				return {
					level: 'urgent',
					title: 'License Expiring',
					message: `Your organization's license expires in ${daysRemaining} days.`,
					className: classes.urgentAlert
				};
			} else if (daysRemaining <= 30) {
				return {
					level: 'warning',
					title: 'License Renewal Reminder',
					message: `Your organization's license expires in ${daysRemaining} days.`,
					className: classes.warningAlert
				};
			}
		}

		// Check user capacity
		if (license?.maxUsers) {
			const currentUsers = myOrganization?.userCount || 0;
			const usagePercentage = (currentUsers / license.maxUsers) * 100;

			if (usagePercentage >= 100) {
				return {
					level: 'critical',
					title: 'User Capacity Reached',
					message: 'Your organization has reached the maximum number of users.',
					className: classes.criticalAlert
				};
			} else if (usagePercentage >= 90) {
				return {
					level: 'warning',
					title: 'Near User Capacity',
					message: `Your organization is at ${Math.round(usagePercentage)}% of user capacity.`,
					className: classes.warningAlert
				};
			}
		}

		return null;
	};

	const warningInfo = getWarningInfo();

	if (!warningInfo) {
		return null;
	}

	// Compact banner for toolbar
	if (compact) {
		return (
			<div
				className={`${warningInfo.className} ${classes.compactBanner}`}
				onClick={() => setExpanded(!expanded)}
			>
				<div className={classes.bannerText}>
					<WarningIcon fontSize="small" />
					<Typography variant="body2">
						{warningInfo.title}: {warningInfo.message}
					</Typography>
				</div>
				<IconButton
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						setDismissed(true);
					}}
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</div>
		);
	}

	// Full alert banner
	return (
		<Collapse in={!dismissed} className={classes.root}>
			<Alert
				severity={warningInfo.level === 'expired' ? 'error' : warningInfo.level === 'critical' ? 'error' : 'warning'}
				className={warningInfo.className}
				action={
					<>
						{user?.isOrganizationAdmin && (
							<Button
								color="inherit"
								size="small"
								component={Link}
								to="/organizations"
							>
								Manage
							</Button>
						)}
						<IconButton
							aria-label="close"
							color="inherit"
							size="small"
							onClick={() => setDismissed(true)}
						>
							<CloseIcon fontSize="inherit" />
						</IconButton>
					</>
				}
			>
				<AlertTitle>{warningInfo.title}</AlertTitle>
				{warningInfo.message}
			</Alert>
		</Collapse>
	);
};

export default LicenseWarningBanner;
